import {
  CONSENT_PLATFORM_LABELS,
  ConsentPlatform,
  ConsentPlatformKey,
  ConsentType,
  LegalConfig,
  LegalDocumentMeta,
  LegalDocumentsMeta,
  LoginConsents
} from 'types/legal';

// Effective dates are calendar dates. Formatting them in UTC keeps "2026-10-01" from
// rendering as 30 Sep west of Greenwich.
export const formatLegalDate = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const formatLegalDateTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// What a reader sees under a document's title: only the effective date. The version
// number is deliberately not shown (the Legal & Consent tab lists versions for admins).
export const documentMetaLine = (meta?: Pick<LegalDocumentMeta, 'effectiveDate'> | null): string => {
  const effective = formatLegalDate(meta?.effectiveDate);
  return effective ? `Effective ${effective}` : '';
};

export const platformLabel = (platform?: ConsentPlatform | null): string => (platform && CONSENT_PLATFORM_LABELS[platform]) || '';

// Shown but never required: carriers reject an SMS opt-in that has to be ticked to
// sign in (Twilio 30923/30931). Declining it signs in as normal; that person isn't texted.
export const OPTIONAL_CONSENT_TYPES: ConsentType[] = ['SMS'];
export const isOptionalConsent = (type: ConsentType): boolean => OPTIONAL_CONSENT_TYPES.includes(type);

// Every box in any of the lists, in display order (Privacy/Terms first).
export const mergeConsentTypes = (...lists: ConsentType[][]): ConsentType[] =>
  (['PRIVACY_TERMS', 'SMS'] as ConsentType[]).filter((type) => lists.some((list) => list.includes(type)));

// A checkbox is shown only when both its switch and this platform's switch are
// explicitly true; anything missing or malformed means "not shown".
export const consentTypesFor = (legal: LegalConfig | null | undefined, platformKey: ConsentPlatformKey): ConsentType[] => {
  const types: ConsentType[] = [];
  const consent = legal?.consent;
  if (consent?.privacyTerms?.enabled === true && consent.privacyTerms.platforms?.[platformKey] === true) {
    types.push('PRIVACY_TERMS');
  }
  if (consent?.sms?.enabled === true && consent.sms.platforms?.[platformKey] === true) {
    types.push('SMS');
  }
  return types;
};

export const legalDocumentsFrom = (legal: LegalConfig | null | undefined): LegalDocumentsMeta => ({
  PRIVACY_POLICY: legal?.documents?.PRIVACY_POLICY ?? null,
  TERMS: legal?.documents?.TERMS ?? null
});

// Only the boxes that were shown and ticked, with the document uids the page displayed.
export const buildLoginConsents = (
  shown: ConsentType[],
  ticked: Partial<Record<ConsentType, boolean>>,
  documents: LegalDocumentsMeta
): LoginConsents => {
  const consents: LoginConsents = {};
  if (shown.includes('PRIVACY_TERMS') && ticked.PRIVACY_TERMS) {
    consents.privacyTerms = {
      accepted: true,
      ...(documents.PRIVACY_POLICY?.uid ? { privacyPolicyUid: documents.PRIVACY_POLICY.uid } : {}),
      ...(documents.TERMS?.uid ? { termsUid: documents.TERMS.uid } : {})
    };
  }
  if (shown.includes('SMS') && ticked.SMS) {
    consents.sms = { accepted: true };
  }
  return consents;
};

// Document links: http(s), mailto and tel only. Everything else (javascript:, data:,
// relative paths) is dropped and the link renders as plain text.
const SAFE_LINK = /^(https?:|mailto:|tel:)/i;
export const safeLegalUrl = (url: string): string => (SAFE_LINK.test(url.trim()) ? url.trim() : '');

// Unwraps the { success, message, data } envelope; a 2xx with success:false still throws.
export const unwrapEnvelope = <T>(response: any): T => {
  if (response && response.success === false) {
    throw new Error(response.message || 'Something went wrong');
  }
  return (response?.data ?? response) as T;
};

export const errorMessage = (error: any, fallback = 'Something went wrong'): string => {
  if (typeof error === 'string' && error) return error;
  const message = error?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message ? message : fallback;
};

export const personPhone = (data: Record<string, any> | null | undefined): string => {
  const phone = String(data?.phoneNumber ?? '').trim();
  if (!phone) return '';
  const code = String(data?.countryCode ?? '')
    .replace(/^\+/, '')
    .trim();
  return code ? `+${code} ${phone}` : phone;
};

export const personName = (data: Record<string, any> | null | undefined): string => {
  const full = [data?.firstName, data?.lastName].filter((v) => typeof v === 'string' && v.trim()).join(' ');
  return full || (typeof data?.name === 'string' ? data.name : '') || '';
};

// Compares phone numbers by digits only, so formatting differences are not a mismatch.
export const samePhone = (a?: string | null, b?: string | null): boolean => {
  const digits = (v?: string | null) => String(v ?? '').replace(/\D/g, '');
  const da = digits(a);
  const db = digits(b);
  if (!da || !db) return true;
  return da === db || da.endsWith(db) || db.endsWith(da);
};
